import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronLeft, ChevronRight, Copy, Maximize2, Menu as MenuIcon, Minimize2, Pencil, Plus, Trash2, UserMinus, X } from "lucide-react";
import { Cpu, MapPin, Network, QrCode, Share2, Wifi, WifiOff, Zap } from "../../components/icons";
import { api } from "../../api/api";
import { asApiError, isUpgradeRequired } from "../../api/client";
import type { SiteCard } from "../../api/types";
import { useAuth } from "../../auth/AuthContext";
import { CloudPowerFlow } from "../../components/CloudPowerFlow";
import { Modal } from "../../components/Modal";
import { LivePowerPills } from "../../components/PowerPills";
import { TimeZoneSelect } from "../../components/TimeZoneSelect";
import { QueryError } from "../../components/UpgradeGate";
import { ErrorBox, PageHeader, Spinner } from "../../components/ui";
import { browserTimeZone, timeAgo, timeLeft } from "../../lib/format";
import { canClaimSite, canShareSite, canWriteSite, planName } from "../../lib/plan";
import { useNow } from "../../lib/useNow";

function ClaimCodeModal({ siteId, onClose }: { siteId: number; onClose: () => void }) {
  const { t } = useTranslation();
  // A claim code is single-use and short-lived, so it is MINTED (a POST,
  // per open) — never served from a query cache, which would hand a second
  // re-link the code the first one already spent.
  const claim = useMutation({ mutationFn: () => api.claimCode(siteId) });
  const minted = useRef(false);
  useEffect(() => {
    if (minted.current) return; // StrictMode double effect: one code per open
    minted.current = true;
    claim.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Ticks once a second while open so the countdown stays live.
  const now = useNow(1000) / 1000;
  const code = claim.data;
  const expired = code != null && code.expires_at <= now;
  return (
    <Modal title={t("sites.claimTitle")} onClose={onClose}>
      {claim.isPending && <Spinner />}
      {claim.error != null && <ErrorBox message={asApiError(claim.error).message} />}
      {code && (
        <div className="space-y-4">
          <div className="flex items-center justify-center gap-3">
            <span className={`text-3xl font-mono tracking-widest ${expired ? "line-through text-muted" : ""}`}>
              {code.code}
            </span>
            <button
              type="button"
              className="btn-ghost !px-2"
              disabled={expired}
              onClick={() => void navigator.clipboard?.writeText(code.code)}
              aria-label="Copy"
            >
              <Copy size={14} />
            </button>
          </div>
          <div className={`text-xs text-center ${expired ? "text-bad" : "text-muted"}`}>
            {expired
              ? t("sites.claimExpired", { defaultValue: "This code has expired." })
              : t("sites.claimExpires", {
                  defaultValue: "Valid for {{left}} · single use",
                  left: timeLeft(code.expires_at, now),
                })}
          </div>
          <p className="text-sm text-muted">{t("sites.claimHint")}</p>
        </div>
      )}
      {!claim.isPending && (
        <div className="flex justify-end mt-4">
          <button type="button" className="btn-ghost" onClick={() => claim.mutate()}>
            {t("sites.claimNew", { defaultValue: "New code" })}
          </button>
        </div>
      )}
    </Modal>
  );
}

/** Edit a site's name, location and time zone. The zone decides where the
 *  site's days and months begin (history day view, analysis month). */
function EditSiteModal({ site, onClose }: { site: SiteCard; onClose: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [name, setName] = useState(site.name);
  const [location, setLocation] = useState(site.location ?? "");
  const [timezone, setTimezone] = useState(site.timezone || browserTimeZone());
  const save = useMutation({
    mutationFn: () =>
      // location as typed — "" clears it (undefined would mean "unchanged").
      api.patchSite(site.id, { name, location: location.trim(), timezone }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["sites"] });
      onClose();
    },
  });
  return (
    <Modal title={t("sites.editSite", { defaultValue: "Edit site" })} onClose={onClose}>
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <div>
          <label className="label" htmlFor="edit-site-name">{t("sites.siteName")}</label>
          <input id="edit-site-name" className="input mt-1" required maxLength={100}
            value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <label className="label" htmlFor="edit-site-location">{t("sites.location")}</label>
          <input id="edit-site-location" className="input mt-1" maxLength={200}
            value={location} onChange={(e) => setLocation(e.target.value)} />
        </div>
        <div>
          <label className="label" htmlFor="edit-site-tz">{t("sites.timezone", { defaultValue: "Time zone" })}</label>
          <TimeZoneSelect id="edit-site-tz" value={timezone} onChange={setTimezone} />
        </div>
        {save.error != null && <ErrorBox message={asApiError(save.error).message} />}
        <button type="submit" className="btn-primary w-full justify-center" disabled={save.isPending}>
          {save.isPending ? <Spinner /> : t("common.save")}
        </button>
      </form>
    </Modal>
  );
}

// Same look as the local Optimization → Battery optimization "Now" card:
// pulsing status dot + "Last seen · Xs" + the LivePowerPills gauge chips.
// The chevron sits in the header row (top-right, next to the status pills)
// and expands to the SAME visualization the local NOW card reveals — the
// dashboard power-flow graphic, fed from the site's latest report. The cloud
// does not sync configured power maxes, so the pills render uncapped.
/** Pending share invitations, shown above the site grid. Only verified
 * accounts see invites (the server returns [] otherwise), so this composes
 * with UnverifiedBanner rather than duplicating its explanation. */
function InviteBanner() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  // Not gated on email_verified here: whether an unverified account may see
  // invites depends on the deployment's ICC_EMAIL_VERIFICATION_REQUIRED, and
  // the server already returns [] when it may not. Deciding it client-side
  // would hide the banner on dev boxes that relax the requirement.
  const invites = useQuery({
    queryKey: ["my-invites"],
    queryFn: api.myInvites,
    refetchInterval: 30_000,
    enabled: !!user,
  });
  const [error, setError] = useState<string | null>(null);
  const done = () => {
    void queryClient.invalidateQueries({ queryKey: ["my-invites"] });
    void queryClient.invalidateQueries({ queryKey: ["sites"] });
  };
  // A failure (expired invite, device consent switched off) is shown — and
  // the list refreshed, since the invite may simply be gone now.
  const failed = (err: unknown) => {
    setError(asApiError(err).message);
    done();
  };
  const accept = useMutation({
    mutationFn: api.acceptInvite,
    onSuccess: () => {
      setError(null);
      done();
    },
    onError: failed,
  });
  const decline = useMutation({
    mutationFn: api.declineInvite,
    onSuccess: () => {
      setError(null);
      done();
    },
    onError: failed,
  });

  if (!invites.data?.length) return error ? <div className="mb-4"><ErrorBox message={error} /></div> : null;
  return (
    <div className="space-y-2 mb-4">
      {error && <ErrorBox message={error} />}
      {invites.data.map((inv) => (
        <div
          key={inv.id}
          className="card p-3 border-brand/40 bg-brand/5 flex items-center justify-between gap-3 flex-wrap"
        >
          <div className="flex items-start gap-2 text-sm min-w-0">
            <Share2 size={15} className="mt-0.5 shrink-0" />
            <span>
              {t("sites.invites.text", {
                defaultValue: "{{inviter}} shared {{site}} with you",
                inviter: inv.inviter,
                site: inv.site_name,
              })}{" "}
              <span className="chip lowercase">
                {t(`sites.share.levels.${inv.role}`, { defaultValue: inv.role })}
              </span>
            </span>
          </div>
          <span className="flex items-center gap-2">
            <button
              type="button"
              className="btn-primary !py-1 text-xs"
              disabled={accept.isPending}
              onClick={() => accept.mutate(inv.id)}
            >
              {accept.isPending ? <Spinner size={12} /> : t("sites.invites.accept", { defaultValue: "Accept" })}
            </button>
            <button
              type="button"
              className="btn-ghost !px-2 !py-1"
              aria-label={t("sites.invites.decline", { defaultValue: "Decline" })}
              disabled={decline.isPending}
              onClick={() => decline.mutate(inv.id)}
            >
              <X size={14} />
            </button>
          </span>
        </div>
      ))}
    </div>
  );
}


/** Fleet-organization invitations addressed to this account. Org membership
 * is consent-based: an org admin invites, the invitee accepts here (or
 * declines). Servers without invitations answer 404 — then nothing shows. */
function OrgInviteBanner() {
  const { t } = useTranslation();
  const { user, refresh } = useAuth();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const invites = useQuery({
    queryKey: ["my-org-invites"],
    queryFn: api.myOrgInvites,
    // A server without org invitations (404) is not polled again.
    refetchInterval: (q) => (q.state.error ? false : 60_000),
    enabled: !!user,
    retry: false,
  });
  const done = async () => {
    void queryClient.invalidateQueries({ queryKey: ["my-org-invites"] });
    void queryClient.invalidateQueries({ queryKey: ["sites"] });
    void queryClient.invalidateQueries({ queryKey: ["fleet"] });
    await refresh(); // `org` on /me drives the Fleet nav entry
  };
  const accept = useMutation({
    mutationFn: api.acceptOrgInvite,
    onSuccess: () => {
      setError(null);
      void done();
    },
    onError: (err) => {
      setError(asApiError(err).message);
      void done();
    },
  });
  const decline = useMutation({
    mutationFn: api.declineOrgInvite,
    onSuccess: () => {
      setError(null);
      void done();
    },
    onError: (err) => setError(asApiError(err).message),
  });
  const list = Array.isArray(invites.data) ? invites.data : [];
  if (!list.length) return error ? <div className="mb-4"><ErrorBox message={error} /></div> : null;
  return (
    <div className="space-y-2 mb-4">
      {error && <ErrorBox message={error} />}
      {list.map((inv) => (
        <div
          key={inv.id}
          className="card p-3 border-brand/40 bg-brand/5 flex items-center justify-between gap-3 flex-wrap"
        >
          <div className="flex items-start gap-2 text-sm min-w-0">
            <Network size={15} className="mt-0.5 shrink-0" />
            <span>
              {t("fleet.invites.text", {
                defaultValue: "{{inviter}} invited you to the organization {{org}}",
                inviter: inv.inviter ?? "—",
                org: inv.org_name ?? "",
              })}{" "}
              <span className="chip lowercase">{t(`fleet.roles.${inv.role}`, { defaultValue: inv.role })}</span>
            </span>
          </div>
          <span className="flex items-center gap-2">
            <button
              type="button"
              className="btn-primary !py-1 text-xs"
              disabled={accept.isPending}
              onClick={() => accept.mutate(inv.id)}
            >
              {accept.isPending ? <Spinner size={12} /> : t("sites.invites.accept", { defaultValue: "Accept" })}
            </button>
            <button
              type="button"
              className="btn-ghost !px-2 !py-1"
              aria-label={t("sites.invites.decline", { defaultValue: "Decline" })}
              disabled={decline.isPending}
              onClick={() => decline.mutate(inv.id)}
            >
              <X size={14} />
            </button>
          </span>
        </div>
      ))}
    </div>
  );
}

/** The site card's three-line menu: Manage / Re-link / Remove. Items render
 * only when the caller may actually use them, so a shared-in member sees a
 * shorter menu and a guest none at all. */
function SiteMenu({
  site,
  canManage,
  onRelink,
  onEdit,
  onRemove,
  onUnshare,
}: {
  site: SiteCard;
  canManage: boolean;
  onRelink: () => void;
  onEdit: () => void;
  onRemove: () => void;
  onUnshare: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const item =
    "flex items-center gap-2 w-full text-left px-3 py-2 text-sm rounded hover:bg-panel2 whitespace-nowrap";
  // Each entry follows what the server will actually allow THIS caller:
  // sharing = owner or org admin, re-linking = owner or org operator+,
  // editing = operator+, removing = owner only.
  const canShare = canShareSite(site);
  const canClaim = canClaimSite(site);
  const canEdit = canWriteSite(site);
  if (!canManage && !canShare && !canClaim && !canEdit && !site.is_owner && !site.can_unshare) return null;
  return (
    <div className="relative">
      <button
        type="button"
        className="shrink-0 grid place-items-center w-7 h-7 rounded-full border border-border text-muted hover:text-text hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t("sites.menu", { defaultValue: "Site menu" })}
        onClick={() => setOpen((o) => !o)}
      >
        <MenuIcon size={15} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 z-20 card p-1 min-w-[10rem] shadow-card" role="menu">
            {/* Icons share ONE color (muted) so the menu reads as a unit —
                only the Remove LABEL carries the destructive red. */}
            {canManage && (
              <button
                type="button"
                className={item}
                onClick={() => navigate(`/app/sites/${site.id}/device/dashboard`)}
              >
                <Cpu size={14} />
                {t("sites.manage")}
              </button>
            )}
            {canShare && (
              <button
                type="button"
                className={item}
                onClick={() => navigate(`/app/sites/${site.id}/share`)}
              >
                <Share2 size={14} />
                {t("sites.tabs.share", { defaultValue: "Share" })}
              </button>
            )}
            {canClaim && (
              <button
                type="button"
                className={item}
                onClick={() => {
                  setOpen(false);
                  onRelink();
                }}
              >
                <QrCode size={14} />
                {t("sites.relink", { defaultValue: "Re-link device" })}
              </button>
            )}
            {canEdit && (
              <button
                type="button"
                className={item}
                onClick={() => {
                  setOpen(false);
                  onEdit();
                }}
              >
                <Pencil size={14} />
                {t("sites.editSite", { defaultValue: "Edit site" })}
              </button>
            )}
            {site.is_owner && (
              <button
                type="button"
                className={`${item} text-bad`}
                onClick={() => {
                  setOpen(false);
                  onRemove();
                }}
              >
                <Trash2 size={14} className="text-muted" />
                {t("sites.removeSite", { defaultValue: "Remove site" })}
              </button>
            )}
            {/* Shared-in sites: drop your own access. Absent for org-sourced
                access, which the API refuses to self-remove. */}
            {site.can_unshare && (
              <button
                type="button"
                className={`${item} text-bad`}
                onClick={() => {
                  setOpen(false);
                  onUnshare();
                }}
              >
                <UserMinus size={14} className="text-muted" />
                {t("sites.unshare", { defaultValue: "Unshare" })}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}


function SiteTile({ site, onFullscreen }: { site: SiteCard; onFullscreen: () => void }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [claimOpen, setClaimOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [confirmUnshare, setConfirmUnshare] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const device = site.devices[0];
  const state = device?.state ?? null;
  const dotColor = !device?.last_seen_at ? "#64748b" : device.online ? "#22c55e" : "#ef4444";
  // Entitlement follows the SITE OWNER's plan, not the viewer's — an org
  // member on Free may still manage a Fleet owner's site. `can_manage` comes
  // from the server so this button matches what the API will actually allow.
  const canManage = !!device && site.can_manage;

  const remove = useMutation({
    mutationFn: () => api.deleteSite(site.id),
    onSuccess: () => {
      setConfirmRemove(false);
      void queryClient.invalidateQueries({ queryKey: ["sites"] });
    },
    onError: (err) => setRemoveError(asApiError(err).message),
  });
  // Unshare = drop your OWN membership ("leave site" on the API).
  const unshare = useMutation({
    mutationFn: () => api.removeSiteMember(site.id, user!.id),
    onSuccess: () => {
      setConfirmUnshare(false);
      void queryClient.invalidateQueries({ queryKey: ["sites"] });
    },
    onError: (err) => setRemoveError(asApiError(err).message),
  });

  // Shared by both card variants (with and without a device).
  const modals = (
    <>
      {claimOpen && <ClaimCodeModal siteId={site.id} onClose={() => setClaimOpen(false)} />}
      {editOpen && <EditSiteModal site={site} onClose={() => setEditOpen(false)} />}
      {confirmRemove && (
        <Modal title={t("sites.removeSite", { defaultValue: "Remove site" })} onClose={() => setConfirmRemove(false)}>
          <div className="space-y-4">
            <p className="text-sm">
              {t("sites.removeConfirm", {
                defaultValue:
                  "Remove “{{name}}” from the cloud? Its synced history, backups and shares are deleted. The local device itself is not affected — it can be re-linked to any site with a new code.",
                name: site.name,
              })}
            </p>
            {removeError && <ErrorBox message={removeError} />}
            <div className="flex justify-end gap-2">
              <button type="button" className="btn" onClick={() => setConfirmRemove(false)}>
                {t("common.cancel")}
              </button>
              <button
                type="button"
                className="btn-primary !bg-bad !border-bad"
                disabled={remove.isPending}
                onClick={() => remove.mutate()}
              >
                {remove.isPending ? <Spinner size={14} /> : t("sites.removeSite", { defaultValue: "Remove site" })}
              </button>
            </div>
          </div>
        </Modal>
      )}
      {confirmUnshare && (
        <Modal title={t("sites.unshare", { defaultValue: "Unshare" })} onClose={() => setConfirmUnshare(false)}>
          <div className="space-y-4">
            <p className="text-sm">
              {t("sites.unshareConfirm", {
                defaultValue:
                  "Stop sharing “{{name}}” with you? You lose access immediately. Only the site owner can invite you again.",
                name: site.name,
              })}
            </p>
            {removeError && <ErrorBox message={removeError} />}
            <div className="flex justify-end gap-2">
              <button type="button" className="btn" onClick={() => setConfirmUnshare(false)}>
                {t("common.cancel")}
              </button>
              <button
                type="button"
                className="btn-primary !bg-bad !border-bad"
                disabled={unshare.isPending}
                onClick={() => unshare.mutate()}
              >
                {unshare.isPending ? <Spinner size={14} /> : t("sites.unshare", { defaultValue: "Unshare" })}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );

  const header = (
    <div className="flex items-center justify-between gap-2">
      <span className="flex items-baseline gap-2 min-w-0">
        {site.can_open ? (
          <Link to={`/app/sites/${site.id}`} className="font-semibold hover:text-brand transition">
            {site.name}
          </Link>
        ) : (
          // Card-only guest: the card renders, nothing opens. No dead links.
          <span className="font-semibold">{site.name}</span>
        )}
        {/* Access someone else granted. The label is deliberately neutral —
            this also covers Fleet/org membership, which did not come from
            sharing. */}
        {!site.is_owner && (
          <span className="chip lowercase" title={t("sites.yourAccess", { defaultValue: "Your access level" })}>
            <Share2 size={13} />
            {t(`sites.share.levels.${site.role}`, { defaultValue: site.role })}
          </span>
        )}
        {site.location && (
          <span className="text-xs text-muted inline-flex items-center gap-1 truncate">
            <MapPin size={11} className="shrink-0" />
            {site.location}
          </span>
        )}
      </span>
      <span className="flex items-center gap-2">
        {state?.inverter && (
          <span className={`chip lowercase ${state.inverter === "online" ? "text-good border-good/40 bg-good/10" : "text-bad border-bad/40 bg-bad/10"}`}>
            <Zap size={13} />
            {t("sites.inverter")}
          </span>
        )}
        {device ? (
          <span className={`chip lowercase ${device.online ? "text-good border-good/40 bg-good/10" : "text-bad border-bad/40 bg-bad/10"}`}>
            {device.online ? <Wifi size={13} /> : <WifiOff size={13} />}
            {device.online ? t("common.online") : t("common.offline")}
          </span>
        ) : (
          <span className="chip lowercase">{t("sites.noDevice")}</span>
        )}
        {device && (
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            className="shrink-0 grid place-items-center w-7 h-7 rounded-full border border-border text-muted hover:text-text hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
          >
            <ChevronDown size={16} className={`transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
        )}
        <SiteMenu
          site={site}
          canManage={canManage}
          onRelink={() => setClaimOpen(true)}
          onEdit={() => setEditOpen(true)}
          onRemove={() => {
            setRemoveError(null);
            setConfirmRemove(true);
          }}
          onUnshare={() => {
            setRemoveError(null);
            setConfirmUnshare(true);
          }}
        />
      </span>
    </div>
  );

  if (!device) {
    return (
      <div className="card p-3 flex flex-col gap-3">
        {header}
        {canClaimSite(site) && (
          <button type="button" className="btn-ghost self-start" onClick={() => setClaimOpen(true)}>
            <QrCode size={14} />
            {t("sites.addDevice")}
          </button>
        )}
        {modals}
      </div>
    );
  }

  return (
    <div className="card p-3 flex flex-col gap-2">
      {header}
      <div className="flex items-center gap-3 flex-wrap">
        <span
          className={`inline-block w-2.5 h-2.5 rounded-full shrink-0 ${device.online ? "animate-pulse" : ""}`}
          style={{ background: dotColor }}
        />
        <span className="text-sm">
          <span className="text-muted">{t("sites.lastSeen")} · </span>
          <b className="tabular-nums">
            {device.last_seen_at ? timeAgo(device.last_seen_at) : t("common.never")}
          </b>
        </span>
        <LivePowerPills
          pvW={state?.pv_w ?? 0}
          // Contract battery sign matches the pills: + discharging / − charging.
          batW={state?.battery_w ?? 0}
          gridW={state?.grid_w ?? 0}
          loadW={state?.load_w ?? 0}
          socPct={state?.soc ?? null}
        />
      </div>
      {open && (
        <div className="mt-1 border-t border-border pt-3 relative">
          {/* Full-window button — same as the local Dashboard → Graphics card. */}
          <button
            type="button"
            onClick={onFullscreen}
            className="absolute top-3 right-0 z-10 inline-flex items-center justify-center h-9 w-9 rounded-full border border-border bg-panel2 text-muted hover:text-text transition"
            aria-label={t("flow.fullscreen")}
            title={t("flow.fullscreen")}
          >
            <Maximize2 size={16} />
          </button>
          {/* Full-width dashboard power flow (2× the previous half-size). */}
          <div className="mx-auto w-full min-w-[260px]">
            <CloudPowerFlow state={state} />
          </div>
        </div>
      )}
      {modals}
    </div>
  );
}

// Full-window power-flow view, same behavior as the local Dashboard →
// Graphics fullscreen: covers the whole window on bg, Escape or the round
// Minimize2 button (top right) exits. Top LEFT: prev/next site navigation +
// the site name. Shows the DETAILED graphic — last-12h usage sparklines in
// every card (the local `large` view), plus generator/relay cards when the
// device reports them.
function SiteFlowOverlay({
  site,
  onClose,
  onPrev,
  onNext,
  canNavigate,
}: {
  site: SiteCard;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
  canNavigate: boolean;
}) {
  const { t } = useTranslation();
  const flowHistory = useQuery({
    queryKey: ["flow-history", site.id],
    queryFn: () => api.siteFlowHistory(site.id),
    refetchInterval: 60_000,
    retry: false,
  });
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (canNavigate && e.key === "ArrowLeft") onPrev();
      if (canNavigate && e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onPrev, onNext, canNavigate]);

  const navBtn =
    "inline-flex items-center justify-center h-9 w-9 rounded-full border border-border bg-panel2 text-muted enabled:hover:text-text disabled:opacity-30 disabled:cursor-not-allowed transition";

  return (
    <div className="fixed inset-0 z-50 bg-bg flex items-center justify-center p-4">
      <div className="absolute top-4 left-4 z-10 flex items-center gap-2">
        <button type="button" className={navBtn} onClick={onPrev} disabled={!canNavigate}
          aria-label={t("sites.prevSite")} title={t("sites.prevSite")}>
          <ChevronLeft size={18} />
        </button>
        <span className="font-semibold text-sm min-w-[8rem] text-center truncate max-w-[16rem]">
          {site.name}
        </span>
        <button type="button" className={navBtn} onClick={onNext} disabled={!canNavigate}
          aria-label={t("sites.nextSite")} title={t("sites.nextSite")}>
          <ChevronRight size={18} />
        </button>
      </div>
      <button type="button" onClick={onClose}
        className={`${navBtn} absolute top-4 right-4 z-10`}
        aria-label={t("flow.exitFullscreen")} title={t("flow.exitFullscreen")}>
        <Minimize2 size={16} />
      </button>
      <div className="w-full max-w-6xl">
        <CloudPowerFlow state={site.devices[0]?.state ?? null} history={flowHistory.data} />
      </div>
    </div>
  );
}

export default function SitesOverview() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [timezone, setTimezone] = useState(browserTimeZone);
  const [error, setError] = useState<string | null>(null);

  const sites = useQuery({ queryKey: ["sites"], queryFn: api.sites, refetchInterval: 10_000 });
  // Ticks every 5 s so the cards' relative "last seen" times stay current
  // between data refetches.
  useNow(5000);

  // Full-window power-flow overlay + prev/next navigation across the sites
  // that have a device to visualize.
  const [fsSiteId, setFsSiteId] = useState<number | null>(null);
  const flowSites = (sites.data ?? []).filter((s) => s.devices.length > 0);
  const fsIndex = flowSites.findIndex((s) => s.id === fsSiteId);
  const fsSite = fsIndex >= 0 ? flowSites[fsIndex] : null;
  const stepSite = (delta: number) => {
    if (!flowSites.length || fsIndex < 0) return;
    const next = (fsIndex + delta + flowSites.length) % flowSites.length;
    setFsSiteId(flowSites[next].id);
  };

  const create = useMutation({
    mutationFn: () => api.createSite({ name, location: location || undefined, timezone }),
    onSuccess: () => {
      setAddOpen(false);
      setName("");
      setLocation("");
      setTimezone(browserTimeZone());
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ["sites"] });
    },
    onError: (err) => {
      setError(
        isUpgradeRequired(err)
          ? t("gate.locked", { plan: planName(err.requiredPlan) })
          : asApiError(err).message,
      );
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    create.mutate();
  }

  return (
    <div>
      <PageHeader
        title={t("sites.title")}
        subtitle={t("sites.subtitle")}
        right={
          <button
            type="button"
            className="btn-primary"
            onClick={() => {
              // A fresh dialog: last attempt's error must not greet the next.
              setError(null);
              create.reset();
              setAddOpen(true);
            }}
          >
            <Plus size={14} />
            {t("sites.addSite")}
          </button>
        }
      />
      <InviteBanner />
      <OrgInviteBanner />
      {sites.isLoading && <Spinner />}
      {sites.error != null && <QueryError error={sites.error} />}
      {sites.data && sites.data.length === 0 && (
        <div className="card p-8 text-center">
          <h3 className="font-semibold">{t("sites.noSites")}</h3>
          <p className="text-sm text-muted mt-1">{t("sites.noSitesHint")}</p>
        </div>
      )}
      {/* Full-width cards (2× the previous xl two-column width). */}
      <div className="grid gap-4 grid-cols-1">
        {sites.data?.map((site) => (
          <SiteTile key={site.id} site={site} onFullscreen={() => setFsSiteId(site.id)} />
        ))}
      </div>
      {fsSite && (
        <SiteFlowOverlay
          site={fsSite}
          onClose={() => setFsSiteId(null)}
          onPrev={() => stepSite(-1)}
          onNext={() => stepSite(1)}
          canNavigate={flowSites.length > 1}
        />
      )}
      {addOpen && (
        <Modal title={t("sites.addSite")} onClose={() => setAddOpen(false)}>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <label className="label" htmlFor="site-name">{t("sites.siteName")}</label>
              <input
                id="site-name"
                className="input mt-1"
                required
                maxLength={100}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div>
              <label className="label" htmlFor="site-location">{t("sites.location")}</label>
              <input
                id="site-location"
                className="input mt-1"
                maxLength={200}
                value={location}
                onChange={(e) => setLocation(e.target.value)}
              />
            </div>
            <div>
              <label className="label" htmlFor="site-tz">{t("sites.timezone", { defaultValue: "Time zone" })}</label>
              <TimeZoneSelect id="site-tz" value={timezone} onChange={setTimezone} />
            </div>
            {error && <ErrorBox message={error} />}
            <button type="submit" className="btn-primary w-full justify-center" disabled={create.isPending}>
              {create.isPending ? <Spinner /> : t("common.add")}
            </button>
          </form>
        </Modal>
      )}
    </div>
  );
}
